import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Users, Plus, X, Eye, Send, UserX, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = { active: "Ενεργός", pending: "Εκκρεμεί", terminated: "Τερματισμένος" };
const STATUS_CLS = { active: "bg-[#E2ECE4] text-[#3F6B4C]", pending: "bg-[#F3EADF] text-[#9A6A3A]", terminated: "bg-[#F6E2DD] text-[#A1493A]" };
const ETYPE = { hourly: "Ωρομίσθιος", monthly: "Μισθωτός", seasonal: "Εποχικός" };
let keySeq = 0;
const newKey = () => `w${++keySeq}`;

function age(dob) { if (!dob) return null; const d = new Date(dob); return Math.floor((Date.now() - d) / 31557600000); }

export default function TeamRoster({ embedded }) {
  const qc = useQueryClient();
  const { data: boot } = useQuery({ queryKey: ["team-boot"], queryFn: async () => (await api.get("/team/bootstrap")).data });
  const [tab, setTab] = useState("active");
  const [deptF, setDeptF] = useState("");
  const [showTerm, setShowTerm] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(null);
  const [cardTab, setCardTab] = useState("info");
  const [sp, setSp] = useSearchParams();
  const [reveal, setReveal] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (tab !== "all") p.set("status", tab);
    if (tab === "all" && showTerm) p.set("show_terminated", "true");
    if (deptF) p.set("department_id", deptF);
    if (q) p.set("q", q);
    return p.toString();
  }, [tab, showTerm, deptF, q]);

  const { data } = useQuery({ queryKey: ["team-members", params], queryFn: async () => (await api.get(`/team/members?${params}`)).data });
  const rows = data?.rows || [];
  const canManage = data?.can_manage;
  useEffect(() => {
    const mid = sp.get("member");
    if (!mid || !data?.rows || edit) return;
    const m = data.rows.find((x) => x.id === mid || x.employee_id === mid);
    if (m) {
      setEdit({ ...m, afm: "", amka: "", iban: "", role_ids: m.role_ids || [], wage_rates: (m.wage_rates || []).map((w) => ({ ...w, _k: newKey() })) });
      setCardTab(sp.get("card") || "pay");
      sp.delete("member"); sp.delete("card"); setSp(sp, { replace: true });
    }
  }, [data, sp, edit, setSp]);
  const { user } = useAuth();
  const canWages = data?.can_view_wages;
  const depts = boot?.departments || [];
  const deptName = (id) => depts.find(d => d.id === id)?.name || "—";
  const rolesOf = (d) => (depts.find(x => x.id === d)?.roles) || [];

  const eraseM = useMutation({
    mutationFn: async (id) => (await api.delete(`/team/members/${id}/gdpr-delete`)).data,
    onSuccess: () => { toast.success("Διαγράφηκε οριστικά"); qc.invalidateQueries({ queryKey: ["team-members"] }); qc.invalidateQueries({ queryKey: ["employees"] }); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const saveM = useMutation({
    mutationFn: async (raw) => {
      const m = { ...raw, wage_rates: (raw.wage_rates || []).map(({ _k, ...w }) => w) };
      return m.id ? (await api.put(`/team/members/${m.id}`, m)).data : (await api.post("/team/members", m)).data;
    },
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["team-members"] }); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const bulk = useMutation({
    mutationFn: async (body) => (await api.post("/team/members/bulk", body)).data,
    onSuccess: (r) => { toast.success(`Ενημερώθηκαν ${r.updated}`); qc.invalidateQueries({ queryKey: ["team-members"] }); setSel([]); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const doReveal = useMutation({
    mutationFn: async (id) => (await api.post(`/team/members/${id}/reveal`)).data,
    onSuccess: (r) => setReveal(r),
    onError: (e) => toast.error(apiErr(e)),
  });
  const resend = useMutation({
    mutationFn: async (id) => (await api.post(`/team/members/${id}/resend-invite`)).data,
    onSuccess: () => toast.success("Η πρόσκληση στάλθηκε ξανά"),
    onError: (e) => toast.error(apiErr(e)),
  });

  const blank = { name: "", email: "", mobile: "+30", dob: "", department_id: depts[0]?.id || "", role_ids: [], access_level: "employee", status: "active", employment_type: "hourly", hire_date: "", wage_rates: [], afm: "", amka: "", iban: "", location_id: boot?.locations?.[0]?.id || "", monthly_base: 0, taf_pct: 1, tribe_pct: 0, overtime_rate: 0, extra_day_rate: 0, position: "" };

  return (
    <div className="space-y-6" data-testid="team-roster-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {!embedded && <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Team &amp; Time</p>}
          <h1 className={`font-serif ${embedded ? "text-2xl" : "text-3xl md:text-4xl mt-1"} text-[#45423C] inline-flex items-center gap-2`}><Users className="h-6 w-6" /> {embedded ? "Εργαζόμενοι" : "Ομάδα"}</h1>
        </div>
        {canManage && <button data-testid="member-add" onClick={() => { setCardTab("info"); setEdit({ ...blank }); }}
          className="rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Προσθήκη μέλους</button>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["active", "pending", "all"].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-[#45423C] text-white" : "bg-white border border-[#E3DED4] text-[#6F6A60]"}`}>
            {t === "active" ? "Ενεργοί" : t === "pending" ? "Εκκρεμείς" : "Όλοι"}</button>
        ))}
        {tab === "all" && (
          <label className="flex items-center gap-2 text-sm text-[#6F6A60] ml-1" data-testid="show-terminated">
            <input type="checkbox" checked={showTerm} onChange={(e) => setShowTerm(e.target.checked)} /> Εμφάνιση τερματισμένων</label>
        )}
        <select data-testid="dept-filter" value={deptF} onChange={(e) => setDeptF(e.target.value)} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm ml-auto">
          <option value="">Όλα τα τμήματα</option>
          {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <input data-testid="team-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση…" className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm w-40" />
      </div>

      {sel.length > 0 && canManage && (
        <div className="rounded-xl bg-[#45423C] text-white px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
          <span className="text-sm">{sel.length} επιλεγμένα</span>
          <button data-testid="bulk-terminate" onClick={() => bulk.mutate({ ids: sel, action: "terminate" })} className="text-sm underline">Τερματισμός</button>
          {user?.role === "owner" && (
            <button data-testid="bulk-erase" onClick={() => { if (window.confirm(`ΟΡΙΣΤΙΚΗ διαγραφή ${sel.length} μελών;\n\nΣβήνονται από Team, μισθοδοσία και πρόγραμμα (κρατείται μόνο backup). Δεν αναιρείται.`)) bulk.mutate({ ids: sel, action: "erase" }); }} className="text-sm underline text-[#F6C4B8]">Οριστική διαγραφή</button>
          )}
          <button data-testid="bulk-invite" onClick={() => bulk.mutate({ ids: sel, action: "invite" })} className="text-sm underline">Αποστολή πρόσκλησης</button>
          <button onClick={() => setSel([])} className="ml-auto text-sm">Καθαρισμός</button>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Όνομα</th><th className="px-3 py-3">Τμήμα</th>
                <th className="px-3 py-3">Τύπος</th><th className="px-3 py-3">Κατάσταση</th>
                {canWages && <th className="px-3 py-3">Αμοιβή</th>}
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Κανένα μέλος.</td></tr>}
              {rows.map(m => {
                const a = age(m.dob); const minor = a !== null && a < 18;
                return (
                  <tr key={m.id} className="border-t border-[#E3DED4] hover:bg-[#FAF8F4]" data-testid={`member-row-${m.id}`}>
                    <td className="px-3 py-2">{canManage && <input type="checkbox" data-testid={`sel-${m.id}`} checked={sel.includes(m.id)} onChange={(e) => setSel(s => e.target.checked ? [...s, m.id] : s.filter(x => x !== m.id))} />}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => { if (!canManage) return; setCardTab("info"); setEdit({ ...m, afm: "", amka: "", iban: "", role_ids: m.role_ids || [], wage_rates: (m.wage_rates || []).map((w) => ({ ...w, _k: newKey() })) }); }} className="font-medium text-[#45423C] text-left">{m.name}</button>
                      <div className="text-[11px] text-[#8A8577]">{m.mobile}{minor && <span className="ml-2 text-[#A1493A] font-semibold" data-testid={`minor-${m.id}`}>⚠ Ανήλικος</span>}</div>
                    </td>
                    <td className="px-3 py-2 text-[#6F6A60]">{deptName(m.department_id)}</td>
                    <td className="px-3 py-2 text-[#6F6A60]">{ETYPE[m.employment_type] || m.employment_type}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CLS[m.status]}`}>{STATUS_LABEL[m.status]}</span></td>
                    {canWages && <td className="px-3 py-2 text-[#6F6A60]">{(m.wage_rates || []).map(w => `€${w.rate}/${w.unit === "month" ? "μήνα" : "ώρα"}`).join(", ") || "—"}</td>}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {m.status === "pending" && canManage && <button data-testid={`resend-${m.id}`} onClick={() => resend.mutate(m.id)} title="Επαναποστολή" className="text-[#6E7A63] px-1"><Send className="h-4 w-4 inline" /></button>}
                      {boot?.me?.access_level === "account_owner" && <button data-testid={`reveal-${m.id}`} onClick={() => doReveal.mutate(m.id)} title="Ευαίσθητα στοιχεία" className="text-[#C86235] px-1"><Eye className="h-4 w-4 inline" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {reveal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="reveal-modal" onClick={() => setReveal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl text-[#45423C] mb-1">Ευαίσθητα στοιχεία</h2>
            <p className="text-[11px] text-[#A1493A] mb-4">Η προβολή καταγράφηκε (GDPR).</p>
            <div className="space-y-2 text-sm"><div><b>ΑΦΜ:</b> {reveal.afm || "—"}</div><div><b>ΑΜΚΑ:</b> {reveal.amka || "—"}</div><div><b>IBAN:</b> {reveal.iban || "—"}</div></div>
            <button onClick={() => setReveal(null)} className="mt-5 w-full rounded-xl bg-[#45423C] text-white py-2.5 text-sm">Κλείσιμο</button>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto" data-testid="member-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-[#45423C]">{edit.id ? "Καρτέλα εργαζομένου" : "Νέο μέλος"}</h2>
              <button onClick={() => setEdit(null)}><X className="h-5 w-5 text-[#6F6A60]" /></button>
            </div>
            <div className="flex gap-1 mb-4 border-b border-[#E3DED4]" data-testid="card-tabs">
              {[["info", "Στοιχεία & Team"], ...(canWages ? [["pay", "Μισθοδοσία"]] : []), ["sensitive", "Ευαίσθητα"]].map(([k, l]) => (
                <button key={k} data-testid={`card-tab-${k}`} onClick={() => setCardTab(k)}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${cardTab === k ? "border-[#C86235] text-[#45423C]" : "border-transparent text-[#8A8577] hover:text-[#45423C]"}`}>{l}</button>
              ))}
            </div>
            {cardTab === "info" && (<>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Όνομα" testid="m-name" value={edit.name} onChange={v => setEdit({ ...edit, name: v })} span />
              <Field label="Email" testid="m-email" value={edit.email} onChange={v => setEdit({ ...edit, email: v })} />
              <Field label="Κινητό (+30)" testid="m-mobile" value={edit.mobile} onChange={v => setEdit({ ...edit, mobile: v })} />
              <Field label="Ημ. γέννησης" testid="m-dob" type="date" value={edit.dob} onChange={v => setEdit({ ...edit, dob: v })} />
              <Field label="Ημ. πρόσληψης" testid="m-hire" type="date" value={edit.hire_date} onChange={v => setEdit({ ...edit, hire_date: v })} />
              <Sel label="Τμήμα" testid="m-dept" value={edit.department_id} onChange={v => setEdit({ ...edit, department_id: v, role_ids: [] })} opts={depts.map(d => [d.id, d.name])} />
              <Sel label="Ρόλος" testid="m-role" value={edit.role_ids?.[0] || ""} onChange={v => setEdit({ ...edit, role_ids: v ? [v] : [] })} opts={rolesOf(edit.department_id).map(r => [r.id, r.name])} />
              <Sel label="Τύπος απασχόλησης" testid="m-etype" value={edit.employment_type} onChange={v => setEdit({ ...edit, employment_type: v })} opts={[["hourly", "Ωρομίσθιος"], ["monthly", "Μισθωτός"], ["seasonal", "Εποχικός"]]} />
              <Sel label="Επίπεδο πρόσβασης" testid="m-access" value={edit.access_level} onChange={v => setEdit({ ...edit, access_level: v })} opts={(boot?.access_levels || []).map(a => [a.value, a.label])} />
              <Sel label="Κατάσταση" testid="m-status" value={edit.status} onChange={v => setEdit({ ...edit, status: v })} opts={[["active", "Ενεργός"], ["pending", "Εκκρεμεί"], ["terminated", "Τερματισμένος"]]} />
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold mb-1">Αμοιβή ανά ρόλο</div>
              {(edit.wage_rates || []).map((w, i) => (
                <div key={w._k || i} className="flex gap-2 mb-2 items-center">
                  <input data-testid={`m-wage-role-${i}`} placeholder="Ρόλος" value={w.role_name} onChange={e => { const r = [...edit.wage_rates]; r[i] = { ...r[i], role_name: e.target.value }; setEdit({ ...edit, wage_rates: r }); }} className="flex-1 rounded-xl border border-[#E3DED4] px-3 py-2" />
                  <input data-testid={`m-wage-rate-${i}`} type="number" placeholder="€" value={w.rate} onChange={e => { const r = [...edit.wage_rates]; r[i] = { ...r[i], rate: parseFloat(e.target.value) || 0 }; setEdit({ ...edit, wage_rates: r }); }} className="w-24 rounded-xl border border-[#E3DED4] px-3 py-2" />
                  <select value={w.unit} onChange={e => { const r = [...edit.wage_rates]; r[i] = { ...r[i], unit: e.target.value }; setEdit({ ...edit, wage_rates: r }); }} className="rounded-xl border border-[#E3DED4] px-2 py-2"><option value="hour">/ώρα</option><option value="month">/μήνα</option></select>
                  <button onClick={() => setEdit({ ...edit, wage_rates: edit.wage_rates.filter((_, j) => j !== i) })} className="text-[#A1493A]"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <button data-testid="m-wage-add" onClick={() => setEdit({ ...edit, wage_rates: [...(edit.wage_rates || []), { role_name: "", rate: 0, unit: "hour", _k: newKey() }] })} className="text-sm text-[#6E7A63] font-semibold">+ Προσθήκη αμοιβής</button>
            </div>
            </>)}

            {canWages && cardTab === "pay" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Μισθοδοσία — ίδια εγγραφή με Payroll / Budget</div>
                <Field label="Θέση (μισθοδοσία)" testid="m-position" value={edit.position} onChange={v => setEdit({ ...edit, position: v })} />
                <Field label="Βασικός μισθός (€)" testid="m-base" type="number" value={edit.monthly_base} onChange={v => setEdit({ ...edit, monthly_base: parseFloat(v) || 0 })} />
                <Field label="Υπερωρία (€/ώρα)" testid="m-ot" type="number" value={edit.overtime_rate} onChange={v => setEdit({ ...edit, overtime_rate: parseFloat(v) || 0 })} />
                <Field label="Υπερημερία (€/ημέρα)" testid="m-xd" type="number" value={edit.extra_day_rate} onChange={v => setEdit({ ...edit, extra_day_rate: parseFloat(v) || 0 })} />
                <Field label="Μερίδιο TAF (%)" testid="m-taf" type="number" value={Math.round((Number(edit.taf_pct) || 0) * 100)} onChange={v => setEdit({ ...edit, taf_pct: (parseFloat(v) || 0) / 100 })} />
                <Field label="Μερίδιο TRIBE (%)" testid="m-tribe" type="number" value={Math.round((Number(edit.tribe_pct) || 0) * 100)} onChange={v => setEdit({ ...edit, tribe_pct: (parseFloat(v) || 0) / 100 })} />
              </div>
            )}

            {cardTab === "sensitive" && (
            <div className="rounded-xl bg-[#FBF8F3] border border-[#EEE7DA] p-3">
              <div className="text-xs uppercase tracking-wider text-[#8A6A3A] font-semibold mb-2">Ευαίσθητα (κρυπτογραφούνται)</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <Field label="ΑΦΜ" testid="m-afm" value={edit.afm} onChange={v => setEdit({ ...edit, afm: v })} ph={edit.has_afm ? "•••• αποθηκευμένο" : ""} />
                <Field label="ΑΜΚΑ" testid="m-amka" value={edit.amka} onChange={v => setEdit({ ...edit, amka: v })} ph={edit.has_amka ? "•••• αποθηκευμένο" : ""} />
                <Field label="IBAN" testid="m-iban" value={edit.iban} onChange={v => setEdit({ ...edit, iban: v })} ph={edit.has_iban ? "•••• αποθηκευμένο" : ""} />
              </div>
            </div>
            )}

            <div className="mt-6 flex gap-3">
              <button data-testid="member-save" onClick={() => { const p = { ...edit }; ["afm", "amka", "iban"].forEach(k => { if (!p[k]) delete p[k]; }); saveM.mutate(p); }} disabled={!edit.name.trim() || saveM.isPending}
                className="flex-1 rounded-xl bg-[#45423C] text-white py-2.5 text-sm font-semibold disabled:opacity-60">Αποθήκευση</button>
              {edit.id && <button data-testid="member-terminate" onClick={() => { if (window.confirm("Τερματισμός μέλους;")) saveM.mutate({ ...edit, status: "terminated" }); }} className="rounded-xl border border-[#A1493A] text-[#A1493A] px-4 py-2.5 text-sm inline-flex items-center gap-1"><UserX className="h-4 w-4" /> Τερματισμός</button>}
              {edit.id && <button data-testid="member-erase" onClick={() => { if (window.confirm(`ΟΡΙΣΤΙΚΗ διαγραφή του «${edit.name}»;\n\nΣβήνεται από Team, μισθοδοσία και πρόγραμμα (κρατείται μόνο backup). Δεν αναιρείται.`)) eraseM.mutate(edit.id); }} className="rounded-xl bg-[#A1493A] text-white px-4 py-2.5 text-sm inline-flex items-center gap-1"><Trash2 className="h-4 w-4" /> Οριστική διαγραφή</button>}
              <button onClick={() => setEdit(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = "text", span, ph }) => (
  <div className={span ? "col-span-2" : ""}>
    <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">{label}</label>
    <input data-testid={testid} type={type} value={value || ""} placeholder={ph || ""} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
  </div>
);
const Sel = ({ label, value, onChange, opts, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">{label}</label>
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm bg-white">
      <option value="">—</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
